/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable indent */
import i18next from 'i18next';
import { showAlertToaster } from '../components/EpAlertToaster';
import {
  NOTIFICATION_EMAIL_NOTIFIER_ENDPOINT,
  NOTIFICATION_ENDPOINT,
  NOTIFICATION_GET_ADMISSIONS_PATIENT_DATA_ENDPOINT,
  NOTIFICATION_GET_CATEGORIES_ENDPOINT,
  NOTIFICATION_GET_INCIDENTS_ENDPOINT,
  NOTIFICATION_GET_INCIDENTS_TYPES_ENDPOINT,
  NOTIFICATION_GET_OCCUPATIONS_ENDPOINT,
  NOTIFICATION_GET_PATIENT_DATA_ANONYMIZEDS_ENDPOINT,
  NOTIFICATION_GET_TYPES_CATEGORIES_ENDPOINT,
  NOTIFICATION_GET_TYPES_ENDPOINT,
  NOTIFICATION_PHARMACY_ENDPOINT,
} from '../constants/notificationConstants';
import { useTranslation as t } from '../hooks/useTranslation';
import { IServiceMutationsProps, IServiceQueryProps } from '../interfaces';
import { useTranslationStore } from '../stores';
import {
  apiService,
  hasToken,
  NotificationCreateBody,
  PharmacyCreateBody,
} from '../utils';
import { httpServices } from './httpServices';
import { usePathname } from 'next/navigation';
import { useBundles } from '../hooks/useBundles';

/** Services */

const { get, post } = httpServices;

const notificationGetServices = ({
  key,
  url,
  enabled = true,
  params,
}: IServiceQueryProps) =>
  get({ key, url: apiService() + url, enabled, params });

const notificationPostServices = ({
  data,
  url,
  errorCallbackFunc,
  successCallbackFunc,
}: IServiceMutationsProps) =>
  post({
    data: JSON.stringify(data),
    url: apiService() + url,
    errorCallbackFunc,
    successCallbackFunc,
  });

/** Get requests */

const getPatientData = (
  PatientIdentification: string,
  identificationType: string,
  hospitalId: string,
) => {
  const api = hasToken()
    ? process.env.NEXT_PUBLIC_BASE_API_URL_ADMISSIONS +
      NOTIFICATION_GET_ADMISSIONS_PATIENT_DATA_ENDPOINT
    : process.env.NEXT_PUBLIC_BASE_API_URL_SP +
      'user-not-logged/' +
      NOTIFICATION_GET_PATIENT_DATA_ANONYMIZEDS_ENDPOINT;

  return get({
    key: ['patientData', i18next.language, PatientIdentification, hospitalId],
    url: api,
    params: {
      PatientIdentification: PatientIdentification,
      IdentificationType: identificationType,
      HospitalId: hospitalId,
    },
    enabled: !!PatientIdentification && !!hospitalId,
  });
};

const getNotificationTypes = () =>
  notificationGetServices({
    key: ['notificationTypes', i18next.language],
    url: NOTIFICATION_GET_TYPES_ENDPOINT,
    params: {},
  });

const getNotificationTypesCategories = (notificationTypeId: string) =>
  notificationGetServices({
    key: ['notificationTypesCategories', i18next.language, notificationTypeId],
    url: NOTIFICATION_GET_TYPES_ENDPOINT + notificationTypeId + `/categories`,
    params: {
      ItensPerPage: 100,
    },
    enabled: !!notificationTypeId,
  });

const getNotificationIncidentsTypes = (
  notificationTypeId: string,
  notificationCategoryId: string,
) =>
  notificationGetServices({
    key: [
      'notificationIncidentsTypes',
      i18next.language,
      notificationTypeId,
      notificationCategoryId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_TYPES_ENDPOINT +
      notificationTypeId +
      '/notification-categories/' +
      notificationCategoryId,
    params: {
      ItensPerPage: 100,
    },
    enabled: !!notificationTypeId && !!notificationCategoryId,
  });

const getNotificationIncidentsSites = (notificationIncidentId: string) =>
  notificationGetServices({
    key: [
      'notificationIncidentsSites',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT + notificationIncidentId + '/sites',
    enabled: !!notificationIncidentId,
    params: {},
  });

const getNotificationIncidentsDrugClasses = (notificationIncidentId: string) =>
  notificationGetServices({
    key: [
      'notificationIncidentsDrugClasses',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT +
      notificationIncidentId +
      '/drug-classes',
    params: {
      ItensPerPage: 100,
    },
    enabled: !!notificationIncidentId,
  });

const getNotificationIncidentsRouteAdministrations = (
  notificationIncidentId: string,
) =>
  notificationGetServices({
    key: [
      'notificationIncidentsRouteAdministrations',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT +
      notificationIncidentId +
      '/route-administrations',
    params: {
      ItensPerPage: 100,
    },
    enabled: !!notificationIncidentId,
  });

const getNotificationIncidentsGrades = (notificationIncidentId: string) =>
  notificationGetServices({
    key: [
      'notificationIncidentsGrades',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT + notificationIncidentId + '/grades',
    enabled: !!notificationIncidentId,
    params: {},
  });

const getNotificationIncidentsOptions = (notificationIncidentId: string) =>
  notificationGetServices({
    key: [
      'notificationIncidentsOptions',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT + notificationIncidentId + '/options',
    enabled: !!notificationIncidentId,
    params: {
      ItensPerPage: 100,
    },
  });

const getNotificationIncidentsStagings = (notificationIncidentId: string) =>
  notificationGetServices({
    key: [
      'notificationIncidentsStagings',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT +
      notificationIncidentId +
      '/stagings',
    enabled: !!notificationIncidentId,
    params: {},
  });

const getNotificationIncidentsSides = (notificationIncidentId: string) =>
  notificationGetServices({
    key: [
      'notificationIncidentsSides',
      i18next.language,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_INCIDENTS_ENDPOINT + notificationIncidentId + '/sides',
    enabled: !!notificationIncidentId,
    params: {},
  });

const getNotificationCategoriesBradenScales = (
  notificationCategoryId: string,
) =>
  notificationGetServices({
    key: [
      'notificationCategoriesBradenScales',
      i18next.language,
      notificationCategoryId,
    ],
    url:
      NOTIFICATION_GET_CATEGORIES_ENDPOINT +
      notificationCategoryId +
      '/braden-scales',
    enabled: !!notificationCategoryId,
    params: {},
  });

const getNotificationCategoriesInvasiveDeviceCauseDispossessions = (
  notificationCategoryId: string,
) =>
  notificationGetServices({
    key: [
      'notificationCategoriesInvasiveDeviceCauseDispossessions',
      i18next.language,
      notificationCategoryId,
    ],
    url:
      NOTIFICATION_GET_CATEGORIES_ENDPOINT +
      notificationCategoryId +
      '/invasive-device-cause-dispossessions',
    enabled: !!notificationCategoryId,
    params: {},
  });

const getNotificationCategoriesPatientUses = (notificationCategoryId: string) =>
  notificationGetServices({
    key: [
      'notificationCategoriesPatientUses',
      i18next.language,
      notificationCategoryId,
    ],
    url:
      NOTIFICATION_GET_CATEGORIES_ENDPOINT +
      notificationCategoryId +
      '/patient-uses',
    enabled: !!notificationCategoryId,
    params: {},
  });

const getNotificationCategoriesQuestionsAnswers = (
  notificationCategoryId: string,
  notificationIncidentId: string,
) =>
  notificationGetServices({
    key: [
      'notificationCategoriesQuestionsAnswers',
      i18next.language,
      notificationCategoryId,
      notificationIncidentId,
    ],
    url:
      NOTIFICATION_GET_CATEGORIES_ENDPOINT +
      notificationCategoryId +
      '/incidents/' +
      notificationIncidentId +
      '/questions/answers',
    enabled: !!notificationCategoryId && !!notificationIncidentId,
    params: {},
  });

const getNotificationTypesCategoriesTechnicalComplaints = (
  notificationCategoryId: string,
) =>
  notificationGetServices({
    key: [
      'notificationTypesCategoriesTechnicalComplaints',
      i18next.language,
      notificationCategoryId,
    ],
    url:
      NOTIFICATION_GET_TYPES_CATEGORIES_ENDPOINT +
      notificationCategoryId +
      '/technical-complaints',
    enabled: !!notificationCategoryId,
    params: {},
  });

const getNotificationQualityDeviationsQuestionsAnswers = (
  NotificationTechnicalComplaintId: string,
) =>
  notificationGetServices({
    key: [
      'notificationQualityDeviationsQuestionsAnswers',
      i18next.language,
      NotificationTechnicalComplaintId,
    ],
    url:
      NOTIFICATION_ENDPOINT +
      'technical-complaints/' +
      NotificationTechnicalComplaintId +
      '/questions/answers',
    enabled: !!NotificationTechnicalComplaintId,
    params: {},
  });

const getOccupations = (hospitalId: string | undefined) =>
  notificationGetServices({
    key: ['occupations', i18next.language],
    url: NOTIFICATION_GET_OCCUPATIONS_ENDPOINT,
    params: {
      ItensPerPage: 300,
      hospitalId,
    },
  });

const getDocumentType = (hospitalId: string, enabled: boolean) =>
  notificationGetServices({
    key: ['document', 'type', i18next.language],
    enabled,
    url: 'notifications/identification-type',
    params: {
      hospitalId,
      ItensPerPage: 10,
    },
  });

const getDocumentTypePublic = (hospitalId: string, enabled: boolean) =>
  notificationGetServices({
    key: ['document', 'type', 'public', i18next.language],
    enabled,
    url: 'identification-type',
    params: {
      hospitalId,
      ItensPerPage: 10,
    },
  });

/** Post requests */

const CreateNotification = (
  data: unknown,
  categoryType?: string,
  successCallbackFunc?: any,
  errorCallbackFunc?: () => void,
) => {
  const { translations } = useTranslationStore((state) => state);
  const hasCategoryType = categoryType ? `${categoryType}/` : '';
  const pathname = usePathname();
  const isPublicPath = pathname.includes('Public');

  const hasStandardBundle = useBundles(
    Number(process.env.NEXT_PUBLIC_BUNDLES_STANDARD),
  );

  const isPartialUrl = !hasStandardBundle && !isPublicPath ? 'partial' : '';

  return notificationPostServices({
    url: NOTIFICATION_ENDPOINT + hasCategoryType + isPartialUrl,
    data: NotificationCreateBody(data),
    successCallbackFunc,
    errorCallbackFunc: (error: any) => {
      errorCallbackFunc?.();
      showAlertToaster({
        alertToasterStatus: 'danger',
        alertToasterTitle: t(translations['global'], 'Error'),
        alertToasterContent: error?.message?.message
          ? t(translations['global'], error?.message?.message)
          : `<p>${t(
              translations['global'],
              'An unexpected error occurred while executing the transaction. Please repeat the operation.',
            )}</p>`,
        alertToasterDisplayDuration: 5000,
        alertToasterPlacement: 'top-right',
        buttonOnClickFunc: () => console.log(),
      });
    },
  });
};

const CreatePharmacyNotification = (
  data: unknown,
  successCallbackFunc?: any,
  errorCallbackFunc?: () => void,
) => {
  const { translations } = useTranslationStore((state) => state);

  return notificationPostServices({
    url: NOTIFICATION_PHARMACY_ENDPOINT,
    data: PharmacyCreateBody(data),
    successCallbackFunc,
    errorCallbackFunc: (error: any) => {
      errorCallbackFunc?.();
      showAlertToaster({
        alertToasterStatus: 'danger',
        alertToasterTitle: 'Error',
        alertToasterContent: error?.message?.message
          ? error?.message?.message
          : `<p>${t(
              translations['global'],
              'An unexpected error occurred while executing the transaction. Please repeat the operation.',
            )}</p>`,
        alertToasterDisplayDuration: 5000,
        alertToasterPlacement: 'top-right',
        buttonOnClickFunc: () => console.log(),
      });
    },
  });
};

const CreateEmailNotifier = (data?: unknown) => {
  const { translations } = useTranslationStore((state) => state);

  return notificationPostServices({
    url: hasToken()
      ? 'user-not-logged/' + NOTIFICATION_EMAIL_NOTIFIER_ENDPOINT
      : NOTIFICATION_EMAIL_NOTIFIER_ENDPOINT,
    data: data,
    errorCallbackFunc: () =>
      showAlertToaster({
        alertToasterStatus: 'danger',
        alertToasterTitle: 'Error',
        alertToasterContent: `<p>${t(
          translations['global'],
          'Favor digitar um e-mail válido.',
        )}</p>`,
        alertToasterDisplayDuration: 5000,
        alertToasterPlacement: 'top-right',
        buttonOnClickFunc: () => console.log(),
      }),
  });
};

/** Export */

export const notificationService = {
  getPatientData,
  getNotificationTypes,
  getNotificationTypesCategories,
  getNotificationIncidentsTypes,
  getNotificationIncidentsSites,
  getNotificationIncidentsDrugClasses,
  getNotificationIncidentsRouteAdministrations,
  getNotificationIncidentsGrades,
  getNotificationIncidentsOptions,
  getNotificationIncidentsStagings,
  getNotificationIncidentsSides,
  getNotificationCategoriesBradenScales,
  getNotificationCategoriesInvasiveDeviceCauseDispossessions,
  getNotificationCategoriesPatientUses,
  getNotificationCategoriesQuestionsAnswers,
  getNotificationTypesCategoriesTechnicalComplaints,
  getNotificationQualityDeviationsQuestionsAnswers,
  getOccupations,
  getDocumentType,
  getDocumentTypePublic,
  createNotification: CreateNotification,
  createPharmacyNotification: CreatePharmacyNotification,
  createEmailNotifier: CreateEmailNotifier,
};
